import { useEffect, useState, type PropsWithChildren } from 'react'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { api } from './api'
import { Layout } from './components/Layout'
import { AdminLayout } from './pages/admin/AdminLayout'
import { AdminDashboard } from './pages/admin/AdminDashboard'
import { AdminDeploy } from './pages/admin/AdminDeploy'
import { AdminMetrics } from './pages/admin/AdminMetrics'
import { AdminProjects } from './pages/admin/AdminProjects'
import { AdminSignIn } from './pages/admin/AdminSignIn'
import { Contact } from './pages/Contact'
import { Home } from './pages/Home'
import { NotFound } from './pages/NotFound'
import { Principles } from './pages/Principles'
import { Projects } from './pages/Projects'
import type { AuthStatus } from './types'

export type AuthContextValue = AuthStatus & { loading: boolean; refresh(): Promise<void>; signOut(): Promise<void> }

function PublicPage({ children }: PropsWithChildren) { return <Layout>{children}</Layout> }

function ProtectedAdmin({ auth }: { auth: AuthContextValue }) {
  if (auth.loading) return <div className="auth-loading">Checking administrator access…</div>
  if (!auth.user) return <Navigate to="/admin/sign-in" replace />
  return <AdminLayout auth={auth}/>
}

export function App() {
  const [state, setState] = useState<AuthStatus>({ configured: true, user: null })
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const refresh = async () => { try { setState(await api.auth()) } finally { setLoading(false) } }
  useEffect(() => { void refresh() }, [])
  const auth: AuthContextValue = { ...state, loading, refresh, signOut: async () => { await api.signOut(); await refresh(); navigate('/admin/sign-in') } }
  return <Routes>
    <Route path="/" element={<PublicPage><Home/></PublicPage>}/>
    <Route path="/projects" element={<PublicPage><Projects/></PublicPage>}/>
    <Route path="/principles" element={<PublicPage><Principles/></PublicPage>}/>
    <Route path="/contact" element={<PublicPage><Contact/></PublicPage>}/>
    <Route path="/admin/sign-in" element={<AdminSignIn auth={auth}/>}/>
    <Route path="/admin" element={<ProtectedAdmin auth={auth}/> }>
      <Route index element={<AdminDashboard/>}/>
      <Route path="metrics" element={<AdminMetrics/>}/>
      <Route path="deploy" element={<AdminDeploy/>}/>
      <Route path="projects" element={<AdminProjects/>}/>
    </Route>
    <Route path="*" element={<PublicPage><NotFound/></PublicPage>}/>
  </Routes>
}
